import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  LayoutDashboard, Inbox, ListChecks, PlusCircle, Upload, Radio, LogOut,
  Check, X, Trash2, Play, CalendarClock, MapPin, Plus, Download, Tags, Pencil, Megaphone, Users, Sparkles,
} from "lucide-react";
import Navbar from "@/components/Navbar";
import BulkSubmit from "@/components/BulkSubmit";
import { SponsorsAdmin } from "@/components/Sponsors";
import { OrganiserAccounts } from "@/components/OrganiserAccounts";
import Discover from "@/components/Discover";
import { useAuth } from "@/context/AuthContext";
import { useCities } from "@/context/CitiesContext";
import { useCategories } from "@/context/CategoriesContext";
import { api, API, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";

const TABS = [
  { id: "queue", label: "Approval Queue", icon: Inbox },
  { id: "events", label: "All Events", icon: ListChecks },
  { id: "add", label: "Add Event", icon: PlusCircle },
  { id: "bulk", label: "Bulk Upload", icon: Upload },
  { id: "scrapers", label: "Scrapers", icon: Radio },
  { id: "discover", label: "Discover", icon: Sparkles },
  { id: "categories", label: "Categories", icon: Tags },
  { id: "sponsors", label: "Sponsors", icon: Megaphone },
  { id: "accounts", label: "Organiser Logins", icon: Users },
  { id: "cities", label: "Cities", icon: MapPin },
];

export default function Admin() {
  const { user, loading, logout } = useAuth();
  const navigate = useNavigate();
  const [tab, setTab] = useState("queue");
  const [stats, setStats] = useState({ published: 0, pending: 0, rejected: 0 });

  useEffect(() => {
    if (!loading && !user) navigate("/login");
  }, [user, loading, navigate]);

  const loadStats = () => api.get("/admin/stats").then(({ data }) => setStats(data)).catch(() => {});
  useEffect(() => { if (user) loadStats(); }, [user, tab]);

  if (loading || !user)
    return (
      <div className="App">
        <Navbar />
        <p className="text-center text-zinc-500 overline py-24">Checking access…</p>
      </div>
    );

  return (
    <div className="App">
      <Navbar />
      <div className="max-w-7xl mx-auto px-5 sm:px-8 py-10">
        <div className="flex flex-wrap items-end justify-between gap-4 mb-8">
          <div>
            <p className="overline text-ec-volt mb-1 flex items-center gap-2"><LayoutDashboard size={14} /> Control Room</p>
            <h1 className="font-heading text-5xl tracking-tight leading-none">Admin Dashboard</h1>
          </div>
          <button onClick={() => { logout(); navigate("/"); }} data-testid="admin-logout" className="inline-flex items-center gap-2 overline border border-white/15 px-4 py-2.5 hover:border-ec-red hover:text-ec-red transition-colors">
            <LogOut size={15} /> Logout
          </button>
        </div>

        <div className="grid grid-cols-3 gap-px bg-white/10 border border-white/10 mb-8">
          <Stat label="Published" value={stats.published} testid="stat-published" accent />
          <Stat label="Pending" value={stats.pending} testid="stat-pending" />
          <Stat label="Rejected" value={stats.rejected} testid="stat-rejected" />
        </div>

        <div className="flex flex-wrap gap-2 mb-8 border-b border-white/10 pb-4">
          {TABS.map((t) => (
            <button
              key={t.id}
              data-testid={`admin-tab-${t.id}`}
              onClick={() => setTab(t.id)}
              className={`inline-flex items-center gap-2 overline px-4 py-2.5 border transition-colors ${
                tab === t.id ? "bg-ec-volt text-black border-ec-volt" : "border-white/15 text-zinc-300 hover:border-ec-volt/60"
              }`}
            >
              <t.icon size={15} /> {t.label}
              {t.id === "queue" && stats.pending > 0 && (
                <span className={`ml-1 px-1.5 ${tab === t.id ? "bg-black text-ec-volt" : "bg-ec-red text-white"}`}>{stats.pending}</span>
              )}
            </button>
          ))}
        </div>

        {tab === "queue" && <Queue onChange={loadStats} />}
        {tab === "events" && <AllEvents onChange={loadStats} />}
        {tab === "add" && <AddEvent onChange={() => { loadStats(); setTab("events"); }} />}
        {tab === "bulk" && <BulkSubmit onDone={() => { loadStats(); setTab("queue"); }} />}
        {tab === "scrapers" && <Scrapers onChange={loadStats} />}
        {tab === "discover" && <Discover />}
        {tab === "categories" && <CategoryEditor />}
        {tab === "sponsors" && <SponsorsAdmin />}
        {tab === "accounts" && <OrganiserAccounts />}
        {tab === "cities" && <Cities />}
      </div>
    </div>
  );
}

function Stat({ label, value, testid, accent }) {
  return (
    <div className="bg-ec-surface p-6" data-testid={testid}>
      <p className="overline text-zinc-500">{label}</p>
      <p className={`font-heading text-5xl leading-none mt-2 ${accent ? "text-ec-volt" : ""}`}>{value}</p>
    </div>
  );
}

function fmt(iso) {
  return new Date(iso).toLocaleString("en-GB", { weekday: "short", day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" });
}

const SOURCE_BADGE = { scraped: "Scraped", submission: "Submitted", manual: "Manual", bulk: "CSV", organiser: "Organiser", discovered: "Discovered" };

function Queue({ onChange }) {
  const { sportLabel } = useCategories();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = () => { setLoading(true); api.get("/admin/events", { params: { status: "pending" } }).then(({ data }) => setItems(data)).finally(() => setLoading(false)); };
  useEffect(load, []);

  const act = async (id, action) => {
    try {
      await api.post(`/admin/events/${id}/${action}`);
      toast.success(action === "approve" ? "Event published" : "Event rejected");
      load(); onChange();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  if (loading) return <p className="overline text-zinc-500">Loading…</p>;
  if (!items.length) return <Empty title="Queue is clear" sub="No pending events to review. Run a scraper or wait for public submissions." />;

  return (
    <div className="space-y-3" data-testid="queue-list">
      {items.map((e) => (
        <div key={e.id} data-testid={`queue-item-${e.id}`} className="bg-ec-surface border border-white/10 p-5 flex flex-col sm:flex-row sm:items-center gap-4">
          <img src={e.image} alt="" className="w-full sm:w-28 h-28 sm:h-20 object-cover" />
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <span className="overline bg-white/10 px-2 py-0.5 text-ec-volt">{sportLabel(e.sport)}</span>
              <span className="overline bg-white/10 px-2 py-0.5 text-zinc-400">{SOURCE_BADGE[e.source] || e.source}</span>
              <span className="overline bg-white/10 px-2 py-0.5 text-zinc-400 capitalize">{e.city}</span>
            </div>
            <h3 className="font-heading text-2xl tracking-wide leading-none mt-2">{e.title}</h3>
            <p className="text-sm text-zinc-400 mt-1 flex items-center gap-2"><CalendarClock size={14} /> {fmt(e.start_at)} · {e.venue}</p>
          </div>
          <div className="flex gap-2">
            <button data-testid={`approve-${e.id}`} onClick={() => act(e.id, "approve")} className="inline-flex items-center gap-1.5 overline bg-ec-volt text-black px-4 py-2.5 hover:bg-ec-volt-hover transition-colors">
              <Check size={15} /> Approve
            </button>
            <button data-testid={`reject-${e.id}`} onClick={() => act(e.id, "reject")} className="inline-flex items-center gap-1.5 overline border border-white/15 px-4 py-2.5 text-zinc-300 hover:border-ec-red hover:text-ec-red transition-colors">
              <X size={15} /> Reject
            </button>
          </div>
        </div>
      ))}
    </div>
  );
}

function AllEvents({ onChange }) {
  const { sportLabel } = useCategories();
  const [items, setItems] = useState([]);
  const [filter, setFilter] = useState("all");
  const [loading, setLoading] = useState(true);

  const load = () => {
    setLoading(true);
    const params = filter === "all" ? {} : { status: filter };
    api.get("/admin/events", { params }).then(({ data }) => setItems(data)).finally(() => setLoading(false));
  };
  useEffect(load, [filter]);

  const del = async (id) => {
    try { await api.delete(`/admin/events/${id}`); toast.success("Event deleted"); load(); onChange(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const statusColor = { published: "text-ec-volt", pending: "text-yellow-400", rejected: "text-ec-red" };

  return (
    <div>
      <div className="flex gap-2 mb-5">
        {["all", "published", "pending", "rejected"].map((s) => (
          <button key={s} data-testid={`events-filter-${s}`} onClick={() => setFilter(s)} className={`overline px-3 py-2 border transition-colors ${filter === s ? "bg-ec-volt text-black border-ec-volt" : "border-white/15 text-zinc-300 hover:border-ec-volt/60"}`}>{s}</button>
        ))}
      </div>
      {loading ? <p className="overline text-zinc-500">Loading…</p> : !items.length ? <Empty title="No events" sub="Nothing here yet." /> : (
        <div className="border border-white/10 overflow-x-auto">
          <table className="w-full text-sm" data-testid="events-table">
            <thead className="bg-black/50">
              <tr className="text-left overline text-zinc-500">
                <th className="p-3">Event</th><th className="p-3">City</th><th className="p-3">Sport</th><th className="p-3">When</th><th className="p-3">Status</th><th className="p-3">Source</th><th className="p-3"></th>
              </tr>
            </thead>
            <tbody>
              {items.map((e, i) => (
                <tr key={e.id} data-testid={`event-row-${e.id}`} className={i % 2 ? "bg-white/[0.03]" : ""}>
                  <td className="p-3 font-semibold max-w-xs truncate">{e.title}</td>
                  <td className="p-3 text-zinc-400 capitalize">{e.city}</td>
                  <td className="p-3 text-zinc-400">{sportLabel(e.sport)}</td>
                  <td className="p-3 text-zinc-400 whitespace-nowrap">{fmt(e.start_at)}</td>
                  <td className={`p-3 overline ${statusColor[e.status]}`}>{e.status}</td>
                  <td className="p-3 text-zinc-500">{SOURCE_BADGE[e.source] || e.source}</td>
                  <td className="p-3 text-right">
                    <button data-testid={`delete-${e.id}`} onClick={() => del(e.id)} className="text-zinc-500 hover:text-ec-red transition-colors"><Trash2 size={16} /></button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

const emptyForm = { title: "", sport: "", team: "", venue: "", start_at: "", end_at: "", description: "", ticket_url: "", city: "worcester" };
const inputCls = "w-full bg-black/40 border border-white/10 px-3.5 py-3 text-white placeholder-zinc-600 focus:outline-none focus:border-ec-volt transition-colors";

function AddEvent({ onChange }) {
  const { cities } = useCities();
  const { sports, teams: allOrgs } = useCategories();
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);
  const teams = useMemo(() => (form.sport ? allOrgs.filter((t) => t.sport === form.sport) : []), [form.sport, allOrgs]);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (!form.title || !form.sport || !form.venue || !form.start_at) { toast.error("Fill title, sport, venue and start"); return; }
    setSaving(true);
    try {
      await api.post("/admin/events", {
        ...form, team: form.team || null,
        start_at: new Date(form.start_at).toISOString(),
        end_at: form.end_at ? new Date(form.end_at).toISOString() : null,
      });
      toast.success("Event published to the calendar");
      setForm(emptyForm); onChange();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  return (
    <form onSubmit={submit} className="max-w-2xl space-y-5" data-testid="add-event-form">
      <label className="block"><span className="overline text-zinc-500 mb-2 block">Title *</span>
        <input data-testid="add-title" value={form.title} onChange={set("title")} className={inputCls} /></label>
      <div className="grid sm:grid-cols-2 gap-5">
        <label className="block"><span className="overline text-zinc-500 mb-2 block">City *</span>
          <select data-testid="add-city" value={form.city} onChange={set("city")} className={inputCls}>
            {cities.map((c) => <option key={c.slug} value={c.slug}>{c.name}</option>)}
          </select></label>
        <label className="block"><span className="overline text-zinc-500 mb-2 block">Category *</span>
          <select data-testid="add-sport" value={form.sport} onChange={(e) => setForm({ ...form, sport: e.target.value, team: "" })} className={inputCls}>
            <option value="">Select</option>{sports.map((s) => <option key={s.id} value={s.id}>{s.label}</option>)}
          </select></label>
      </div>
      <div className="grid sm:grid-cols-2 gap-5">
        <label className="block"><span className="overline text-zinc-500 mb-2 block">Team</span>
          <select data-testid="add-team" value={form.team} onChange={set("team")} className={inputCls} disabled={!form.sport}>
            <option value="">Optional</option>{teams.map((t) => <option key={t.id} value={t.id}>{t.label}</option>)}
          </select></label>
        <label className="block"><span className="overline text-zinc-500 mb-2 block">Venue *</span>
          <input data-testid="add-venue" value={form.venue} onChange={set("venue")} className={inputCls} /></label>
      </div>
      <div className="grid sm:grid-cols-2 gap-5">
        <label className="block"><span className="overline text-zinc-500 mb-2 block">Start *</span>
          <input data-testid="add-start" type="datetime-local" value={form.start_at} onChange={set("start_at")} className={inputCls} /></label>
        <label className="block"><span className="overline text-zinc-500 mb-2 block">End</span>
          <input data-testid="add-end" type="datetime-local" value={form.end_at} onChange={set("end_at")} className={inputCls} /></label>
      </div>
      <label className="block"><span className="overline text-zinc-500 mb-2 block">Ticket / Info Link</span>
        <input data-testid="add-ticket" type="url" value={form.ticket_url} onChange={set("ticket_url")} className={inputCls} placeholder="https://…" /></label>
      <label className="block"><span className="overline text-zinc-500 mb-2 block">Description</span>
        <textarea data-testid="add-description" rows={3} value={form.description} onChange={set("description")} className={inputCls} /></label>
      <button type="submit" disabled={saving} data-testid="add-submit" className="inline-flex items-center gap-2 overline bg-ec-volt text-black px-7 py-3.5 hover:-translate-y-0.5 transition-all disabled:opacity-50">
        <PlusCircle size={16} /> {saving ? "Publishing…" : "Publish Event"}
      </button>
    </form>
  );
}

function CategoryEditor() {
  const { sports, teams, refresh } = useCategories();
  const [cat, setCat] = useState({ label: "", color: "#CCFF00" });
  const [org, setOrg] = useState({ label: "", category: "" });

  const addCat = async (e) => {
    e.preventDefault();
    if (!cat.label) { toast.error("Category name required"); return; }
    try { await api.post("/admin/categories", cat); toast.success("Category added"); setCat({ label: "", color: "#CCFF00" }); refresh(); }
    catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };
  const delCat = async (id) => {
    if (!window.confirm("Delete this category and its organisers?")) return;
    await api.delete(`/admin/categories/${id}`); toast.success("Category deleted"); refresh();
  };
  const addOrg = async (e) => {
    e.preventDefault();
    if (!org.label || !org.category) { toast.error("Name and category required"); return; }
    try { await api.post("/admin/organisers", org); toast.success("Organiser added"); setOrg({ label: "", category: "" }); refresh(); }
    catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };
  const delOrg = async (id) => { await api.delete(`/admin/organisers/${id}`); toast.success("Organiser deleted"); refresh(); };

  return (
    <div className="grid lg:grid-cols-2 gap-8">
      <div>
        <h3 className="font-heading text-3xl tracking-wide mb-4">Categories</h3>
        <div className="space-y-2 mb-5" data-testid="categories-list">
          {sports.map((s) => (
            <div key={s.id} data-testid={`category-row-${s.id}`} className="bg-ec-surface border border-white/10 p-3 flex items-center gap-3">
              <span className="w-4 h-4 shrink-0" style={{ backgroundColor: s.color }} />
              <span className="font-semibold flex-1">{s.label}</span>
              <span className="text-xs text-zinc-500 font-mono">{s.id}</span>
              <button data-testid={`category-delete-${s.id}`} onClick={() => delCat(s.id)} className="text-zinc-500 hover:text-ec-red transition-colors"><Trash2 size={15} /></button>
            </div>
          ))}
        </div>
        <form onSubmit={addCat} className="bg-ec-surface border border-white/10 p-5 flex flex-wrap items-end gap-3" data-testid="add-category-form">
          <label className="block flex-1 min-w-[140px]"><span className="overline text-zinc-500 mb-2 block">New category</span>
            <input data-testid="category-label" value={cat.label} onChange={(e) => setCat({ ...cat, label: e.target.value })} className={inputCls} placeholder="e.g. Ice Hockey" /></label>
          <label className="block"><span className="overline text-zinc-500 mb-2 block">Colour</span>
            <input data-testid="category-color" type="color" value={cat.color} onChange={(e) => setCat({ ...cat, color: e.target.value })} className="h-12 w-16 bg-black/40 border border-white/10 cursor-pointer" /></label>
          <button type="submit" data-testid="category-submit" className="inline-flex items-center gap-2 overline bg-ec-volt text-black px-5 py-3 hover:-translate-y-0.5 transition-all"><Plus size={16} /> Add</button>
        </form>
      </div>

      <div>
        <h3 className="font-heading text-3xl tracking-wide mb-4">Organisers</h3>
        <div className="space-y-2 mb-5 max-h-[360px] overflow-y-auto" data-testid="organisers-list">
          {sports.map((s) => {
            const list = teams.filter((t) => t.sport === s.id);
            if (!list.length) return null;
            return (
              <div key={s.id}>
                <p className="overline text-zinc-500 mt-3 mb-1">{s.label}</p>
                {list.map((t) => (
                  <div key={t.id} data-testid={`organiser-row-${t.id}`} className="bg-ec-surface border border-white/10 p-3 flex items-center gap-3 mb-1">
                    <span className="font-semibold flex-1">{t.label}</span>
                    <button data-testid={`organiser-delete-${t.id}`} onClick={() => delOrg(t.id)} className="text-zinc-500 hover:text-ec-red transition-colors"><Trash2 size={15} /></button>
                  </div>
                ))}
              </div>
            );
          })}
        </div>
        <form onSubmit={addOrg} className="bg-ec-surface border border-white/10 p-5 space-y-3" data-testid="add-organiser-form">
          <div className="grid sm:grid-cols-2 gap-3">
            <label className="block"><span className="overline text-zinc-500 mb-2 block">New organiser</span>
              <input data-testid="organiser-label" value={org.label} onChange={(e) => setOrg({ ...org, label: e.target.value })} className={inputCls} placeholder="e.g. Worcester Wolves" /></label>
            <label className="block"><span className="overline text-zinc-500 mb-2 block">Category</span>
              <select data-testid="organiser-category" value={org.category} onChange={(e) => setOrg({ ...org, category: e.target.value })} className={inputCls}>
                <option value="">Select</option>{sports.map((s) => <option key={s.id} value={s.id}>{s.label}</option>)}
              </select></label>
          </div>
          <button type="submit" data-testid="organiser-submit" className="inline-flex items-center gap-2 overline bg-ec-volt text-black px-5 py-3 hover:-translate-y-0.5 transition-all"><Plus size={16} /> Add Organiser</button>
        </form>
      </div>
    </div>
  );
}


function Cities() {
  const { cities, refresh } = useCities();
  const [form, setForm] = useState({ slug: "", name: "", tagline: "", hero: "" });
  const [saving, setSaving] = useState(false);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.slug) { toast.error("Name and slug are required"); return; }
    setSaving(true);
    try {
      await api.post("/admin/cities", form);
      toast.success(`${form.name} added`);
      setForm({ slug: "", name: "", tagline: "", hero: "" });
      refresh();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  return (
    <div className="grid lg:grid-cols-2 gap-6">
      <div>
        <h3 className="font-heading text-2xl tracking-wide mb-4">Live Cities</h3>
        <div className="space-y-3" data-testid="cities-list">
          {cities.map((c) => (
            <div key={c.slug} data-testid={`city-row-${c.slug}`} className="bg-ec-surface border border-white/10 p-4 flex items-center gap-4">
              <img src={c.hero} alt="" className="w-20 h-14 object-cover" />
              <div className="min-w-0">
                <h4 className="font-heading text-xl tracking-wide leading-none">{c.name}</h4>
                <p className="text-xs text-zinc-500 mt-1 font-mono">/{c.slug}</p>
              </div>
              <a href={`/${c.slug}`} target="_blank" rel="noreferrer" className="ml-auto overline text-ec-volt underline text-xs">View</a>
            </div>
          ))}
        </div>
      </div>
      <div>
        <h3 className="font-heading text-2xl tracking-wide mb-4">Add a New City</h3>
        <form onSubmit={submit} className="bg-ec-surface border border-white/10 p-6 space-y-4" data-testid="add-city-form">
          <div className="grid sm:grid-cols-2 gap-4">
            <label className="block"><span className="overline text-zinc-500 mb-2 block">Name *</span>
              <input data-testid="city-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value, slug: form.slug || e.target.value.toLowerCase().trim().replace(/\s+/g, "-") })} className={inputCls} placeholder="Bristol" /></label>
            <label className="block"><span className="overline text-zinc-500 mb-2 block">Slug *</span>
              <input data-testid="city-slug" value={form.slug} onChange={set("slug")} className={inputCls} placeholder="bristol" /></label>
          </div>
          <label className="block"><span className="overline text-zinc-500 mb-2 block">Tagline</span>
            <input data-testid="city-tagline" value={form.tagline} onChange={set("tagline")} className={inputCls} placeholder="Bristol · One City · One Calendar" /></label>
          <label className="block"><span className="overline text-zinc-500 mb-2 block">Hero image URL</span>
            <input data-testid="city-hero" value={form.hero} onChange={set("hero")} className={inputCls} placeholder="https://…" /></label>
          <button type="submit" disabled={saving} data-testid="city-submit" className="inline-flex items-center gap-2 overline bg-ec-volt text-black px-6 py-3 hover:-translate-y-0.5 transition-all disabled:opacity-50">
            <Plus size={16} /> {saving ? "Adding…" : "Add City"}
          </button>
        </form>
      </div>
    </div>
  );
}

function Scrapers({ onChange }) {
  const { cities } = useCities();
  const { sports, teams: allOrgs, sportLabel, teamLabel } = useCategories();
  const [items, setItems] = useState([]);
  const [running, setRunning] = useState(null);
  const empty = { name: "", type: "ics", url: "", sport: "arts", team: "", venue: "", city: "worcester" };
  const [form, setForm] = useState(empty);
  const [saving, setSaving] = useState(false);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  const orgOptions = useMemo(() => (form.sport ? allOrgs.filter((t) => t.sport === form.sport) : []), [form.sport, allOrgs]);

  const load = () => api.get("/admin/scrapers").then(({ data }) => setItems(data));
  useEffect(() => { load(); }, []);

  const toggle = async (id) => { await api.post(`/admin/scrapers/${id}/toggle`); load(); };
  const run = async (id) => {
    setRunning(id);
    try {
      const { data } = await api.post(`/admin/scrapers/${id}/run`);
      data.found > 0 ? toast.success(data.message) : toast(data.message);
      load(); onChange();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setRunning(null); }
  };
  const remove = async (id) => {
    await api.delete(`/admin/scrapers/${id}`);
    toast.success("Source removed");
    load();
  };
  const addSource = async (e) => {
    e.preventDefault();
    if (!form.name || !form.url) { toast.error("Name and URL are required"); return; }
    setSaving(true);
    try {
      await api.post("/admin/scrapers", { ...form, team: form.team || null });
      toast.success("Source added");
      setForm(empty); load();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  const statusBadge = (s) => {
    if (s.status === "failed") return <span className="overline text-ec-red">Failed</span>;
    if (s.status === "ok") return <span className="overline text-ec-volt">OK</span>;
    return <span className="overline text-zinc-500">Idle</span>;
  };

  return (
    <div className="space-y-8">
      <p className="text-sm text-zinc-400 max-w-3xl">
        Scrapers fetch events from a source and drop them into the <span className="text-ec-volt">Approval Queue</span> — nothing goes public until you approve it.
        Two source types are supported: <span className="text-white">iCal feed</span> (a <code className="text-ec-volt">.ics</code> "Add to calendar" / subscribe link) and
        <span className="text-white"> Web page</span> (any page with schema.org event data / JSON-LD). Many club sites don't expose machine-readable data — if a run finds nothing or errors, it's reported honestly below.
      </p>

      <div className="grid sm:grid-cols-2 gap-4" data-testid="scrapers-list">
        {items.map((s) => (
          <div key={s.id} data-testid={`scraper-${s.id}`} className="bg-ec-surface border border-white/10 p-5">
            <div className="flex items-start justify-between gap-3">
              <div className="min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <h3 className="font-heading text-2xl tracking-wide leading-none">{s.name}</h3>
                  <span className="overline bg-white/10 px-2 py-0.5 text-zinc-300">{s.type === "ics" ? "iCal" : "Web"}</span>
                </div>
                <p className="overline text-zinc-500 mt-2">{sportLabel(s.sport)}{s.team ? ` · ${teamLabel(s.team)}` : ""}</p>
                <a href={s.url} target="_blank" rel="noreferrer" className="text-xs text-zinc-500 hover:text-ec-volt underline break-all">{s.url}</a>
              </div>
              <div className="flex flex-col items-end gap-2 shrink-0">
                <button data-testid={`scraper-toggle-${s.id}`} onClick={() => toggle(s.id)} className={`overline px-3 py-1.5 border transition-colors ${s.enabled ? "bg-ec-volt text-black border-ec-volt" : "border-white/15 text-zinc-500"}`}>
                  {s.enabled ? "ON" : "OFF"}
                </button>
                <button data-testid={`scraper-delete-${s.id}`} onClick={() => remove(s.id)} className="text-zinc-500 hover:text-ec-red transition-colors"><Trash2 size={15} /></button>
              </div>
            </div>
            {s.last_error && s.status === "failed" && (
              <p data-testid={`scraper-error-${s.id}`} className="text-xs text-ec-red mt-3 break-words">{s.last_error}</p>
            )}
            <div className="flex items-center justify-between mt-5 pt-4 border-t border-white/10">
              <span className="text-xs text-zinc-500">
                {statusBadge(s)} {s.last_run ? `· ${fmt(s.last_run)} · found ${s.last_found}` : "· never run"}
              </span>
              <button
                data-testid={`scraper-run-${s.id}`}
                onClick={() => run(s.id)}
                disabled={!s.enabled || running === s.id}
                className="inline-flex items-center gap-1.5 overline border border-white/15 px-3 py-2 hover:border-ec-volt hover:text-ec-volt transition-colors disabled:opacity-40"
              >
                <Play size={14} /> {running === s.id ? "Running…" : "Run Now"}
              </button>
            </div>
          </div>
        ))}
      </div>

      <div>
        <h3 className="font-heading text-3xl tracking-wide mb-4">Add a Source</h3>
        <form onSubmit={addSource} className="bg-ec-surface border border-white/10 p-6 space-y-4 max-w-2xl" data-testid="add-scraper-form">
          <div className="grid sm:grid-cols-2 gap-4">
            <label className="block"><span className="overline text-zinc-500 mb-2 block">Name *</span>
              <input data-testid="scraper-name" value={form.name} onChange={set("name")} className={inputCls} placeholder="e.g. Worcester Warriors (iCal)" /></label>
            <label className="block"><span className="overline text-zinc-500 mb-2 block">Type *</span>
              <select data-testid="scraper-type" value={form.type} onChange={set("type")} className={inputCls}>
                <option value="ics">iCal feed (.ics)</option>
                <option value="jsonld">Web page (schema.org / JSON-LD)</option>
              </select></label>
          </div>
          <label className="block"><span className="overline text-zinc-500 mb-2 block">Source URL *</span>
            <input data-testid="scraper-url" type="url" value={form.url} onChange={set("url")} className={inputCls} placeholder="https://… .ics feed or events page" /></label>
          <div className="grid sm:grid-cols-2 gap-4">
            <label className="block"><span className="overline text-zinc-500 mb-2 block">City</span>
              <select data-testid="scraper-city" value={form.city} onChange={set("city")} className={inputCls}>
                {cities.map((c) => <option key={c.slug} value={c.slug}>{c.name}</option>)}
              </select></label>
            <label className="block"><span className="overline text-zinc-500 mb-2 block">Category</span>
              <select data-testid="scraper-sport" value={form.sport} onChange={(e) => setForm({ ...form, sport: e.target.value, team: "" })} className={inputCls}>
                {sports.map((s) => <option key={s.id} value={s.id}>{s.label}</option>)}
              </select></label>
          </div>
          <div className="grid sm:grid-cols-2 gap-4">
            <label className="block"><span className="overline text-zinc-500 mb-2 block">Organiser</span>
              <select data-testid="scraper-team" value={form.team} onChange={set("team")} className={inputCls}>
                <option value="">Optional</option>{orgOptions.map((t) => <option key={t.id} value={t.id}>{t.label}</option>)}
              </select></label>
            <label className="block"><span className="overline text-zinc-500 mb-2 block">Default venue</span>
              <input data-testid="scraper-venue" value={form.venue} onChange={set("venue")} className={inputCls} placeholder="Used if the feed has no location" /></label>
          </div>
          <button type="submit" disabled={saving} data-testid="scraper-submit" className="inline-flex items-center gap-2 overline bg-ec-volt text-black px-6 py-3 hover:-translate-y-0.5 transition-all disabled:opacity-50">
            <Plus size={16} /> {saving ? "Adding…" : "Add Source"}
          </button>
        </form>
      </div>
    </div>
  );
}

function Empty({ title, sub }) {
  return (
    <div className="border border-white/10 bg-ec-surface p-12 text-center">
      <p className="font-heading text-3xl text-zinc-400">{title}</p>
      <p className="text-zinc-500 mt-2">{sub}</p>
    </div>
  );
}
